<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { policyService } from '@/services/api';
import type { PolicyBinderAIStatusBinder } from '@/services/api';
import { ExternalLink } from '@lucide/vue';

const props = defineProps<{
  policyId: number;
  modelValue: boolean;
  // See PolicyAIStatusCell: present inside a client's policy list, absent elsewhere.
  accountId?: number | string | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const loading = ref(true);
const error = ref<string | null>(null);
const binders = ref<PolicyBinderAIStatusBinder[]>([]);

async function fetchStatus() {
  loading.value = true;
  error.value = null;
  try {
    const resp = await policyService.binderAIStatus(props.policyId);
    binders.value = resp.binders;
  } catch (e) {
    error.value = (e as Error)?.message ?? 'Failed to load binder status';
  } finally {
    loading.value = false;
  }
}

function close() {
  emit('update:modelValue', false);
}

function reviewRoute(promptResultId: number) {
  return props.accountId
    ? { name: 'account-binder-review', params: { id: String(props.accountId), promptResultId: String(promptResultId) } }
    : { path: '/ai-results', query: { resultId: promptResultId } };
}

onMounted(() => {
  if (props.modelValue) fetchStatus();
});

watch(
  () => props.modelValue,
  (open) => {
    if (open) fetchStatus();
  },
);
</script>

<template>
  <dialog
    v-if="modelValue"
    open
    class="modal modal-open">
    <div class="modal-box max-w-2xl">
      <h2 class="mb-4">AI Status by Binder</h2>

      <div v-if="loading">Loading…</div>
      <div
        v-else-if="error"
        class="text-red-700">
        Error: {{ error }}
      </div>
      <div v-else-if="binders.length === 0">No AI-processed binders for this policy.</div>

      <table
        v-else
        class="apptable dense w-full">
        <thead>
          <tr>
            <td>Binder</td>
            <td>AI Status</td>
            <td></td>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="b in binders"
            :key="b.documentID">
            <td>{{ b.documentName }}</td>
            <td>
              <span :class="b.status === 'validated' ? 'text-green-700' : 'text-amber-700'">
                {{ b.status === 'validated' ? 'Validated' : 'Unvalidated' }}
              </span>
            </td>
            <td>
              <router-link
                :to="reviewRoute(b.aiPromptResultID)"
                class="inline-flex text-primary hover:opacity-70"
                aria-label="View AI Review"
                title="View AI Review"
                @click="close">
                <ExternalLink :size="20" />
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="modal-action">
        <button
          class="btn"
          type="button"
          @click="close">
          Close
        </button>
      </div>
    </div>
    <form
      method="dialog"
      class="modal-backdrop">
      <button
        type="button"
        @click="close">
        close
      </button>
    </form>
  </dialog>
</template>
